import React, { useEffect, useState } from "react";
import { useAuth } from "../lib/store";
import { Navigate, Link } from "react-router-dom";
import { api, brl } from "../lib/api";
import { toast } from "sonner";
import { Cake, Heart, Sparkles, Instagram, Package, Plus, Trash2 } from "lucide-react";

export default function CustomerAccount() {
  const { user, loading, logout, refresh } = useAuth();
  const [orders, setOrders] = useState([]);
  const [profile, setProfile] = useState(null);
  const [followBusy, setFollowBusy] = useState(false);

  useEffect(() => {
    if (user) {
      setProfile({
        name: user.name, phone: user.phone,
        birthday: user.birthday || "", anniversary: user.anniversary || "",
        dating_start: user.dating_start || "", instagram: user.instagram || "",
        special_dates: user.special_dates || [],
      });
      api.get("/customers/me/orders").then(({ data }) => setOrders(data)).catch(() => {});
    }
  }, [user]);

  if (loading) return <div className="py-24 text-center text-[#6B7280]">Carregando…</div>;
  if (!user) return <Navigate to="/entrar" replace />;

  const set = (k, v) => setProfile((p) => ({ ...p, [k]: v }));
  const addSpecial = () => set("special_dates", [...(profile.special_dates || []), { label: "", date: "" }]);
  const setSpecial = (i, k, v) => set("special_dates", profile.special_dates.map((s, idx) => idx === i ? { ...s, [k]: v } : s));
  const rmSpecial = (i) => set("special_dates", profile.special_dates.filter((_, idx) => idx !== i));

  const save = async () => {
    try {
      await api.put("/customers/me", profile);
      toast.success("Perfil salvo");
      refresh();
    } catch (e) { toast.error("Erro ao salvar"); }
  };

  const followInstagram = async () => {
    window.open("https://instagram.com/eternizaimas", "_blank", "noopener");
    setFollowBusy(true);
    try {
      const { data } = await api.post("/customers/instagram-follow", {});
      if (data.granted) toast.success(`Cupom ${data.coupon_code} liberado (${data.percent_off}% off)`);
      else toast.info("Cupom já estava liberado no seu perfil");
      refresh();
    } catch (e) { toast.error("Não foi possível liberar o cupom"); }
    finally { setFollowBusy(false); }
  };

  return (
    <div className="mx-auto max-w-5xl px-6 py-14" data-testid="customer-account-root">
      <div className="flex items-center justify-between">
        <div>
          <div className="eyebrow">Minha conta</div>
          <h1 className="mt-2 font-display text-4xl text-[#0D1B2A]">Olá, {user.name?.split(" ")[0]}</h1>
        </div>
        <button onClick={logout} className="text-sm text-[#6B7280] hover:text-[#0D1B2A]">Sair</button>
      </div>

      {/* Instagram card */}
      <div className="mt-8 bg-[#0D1B2A] text-white rounded-3xl p-6 flex flex-wrap items-center gap-6 justify-between">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-full bg-white/10 flex items-center justify-center"><Instagram size={22} className="text-[#D4AF37]" /></div>
          <div>
            <div className="eyebrow text-[#D4AF37]">Cupom Instagram</div>
            <div className="font-display text-xl mt-1">{user.instagram_followed ? "Você já segue! Cupom EUSIGO10 ativo" : "Siga @eternizaimas e ganhe 10% off"}</div>
          </div>
        </div>
        {!user.instagram_followed && (
          <button data-testid="account-follow-btn" onClick={followInstagram} disabled={followBusy} className="btn-gold">
            {followBusy ? "..." : "Segui! Liberar cupom"}
          </button>
        )}
        {user.instagram_followed && (
          <div className="bg-[#D4AF37] text-[#0D1B2A] rounded-2xl px-4 py-2 font-display text-xl tracking-widest">EUSIGO10</div>
        )}
      </div>

      {/* Profile */}
      {profile && (
        <div className="mt-8 bg-white rounded-3xl p-6 border border-[#0D1B2A]/5">
          <div className="font-display text-2xl">Seus dados</div>
          <div className="mt-4 grid sm:grid-cols-2 gap-4">
            <Field l="Nome" v={profile.name} onC={(v) => set("name", v)} />
            <Field l="WhatsApp" v={profile.phone} onC={(v) => set("phone", v)} />
            <Field l="Instagram (@)" v={profile.instagram} onC={(v) => set("instagram", v)} />
            <Field l="Aniversário" type="date" v={profile.birthday} onC={(v) => set("birthday", v)} icon={Cake} />
            <Field l="Casamento" type="date" v={profile.anniversary} onC={(v) => set("anniversary", v)} icon={Heart} />
            <Field l="Início do namoro" type="date" v={profile.dating_start} onC={(v) => set("dating_start", v)} icon={Sparkles} />
          </div>

          <div className="mt-6">
            <div className="flex items-center justify-between">
              <div className="eyebrow">Outras datas especiais</div>
              <button onClick={addSpecial} data-testid="add-special-date" className="text-sm inline-flex items-center gap-1 text-[#D4AF37]"><Plus size={14} /> adicionar</button>
            </div>
            <div className="mt-3 space-y-2">
              {(profile.special_dates || []).map((s, i) => (
                <div key={i} className="grid sm:grid-cols-[1fr_180px_40px] gap-2">
                  <input placeholder="Nome (ex.: nascimento do João)" className="input-luxe" value={s.label} onChange={(e) => setSpecial(i, "label", e.target.value)} />
                  <input type="date" className="input-luxe" value={s.date} onChange={(e) => setSpecial(i, "date", e.target.value)} />
                  <button type="button" onClick={() => rmSpecial(i)} className="text-[#6B7280] hover:text-[#E66E6E]"><Trash2 size={16} /></button>
                </div>
              ))}
              {!(profile.special_dates || []).length && <div className="text-xs text-[#6B7280]">Ex.: dia do primeiro encontro, aniversários dos filhos, mesversário...</div>}
            </div>
          </div>

          <button onClick={save} data-testid="save-profile" className="mt-6 btn-gold">Salvar alterações</button>
        </div>
      )}

      {/* Orders */}
      <div className="mt-8 bg-white rounded-3xl p-6 border border-[#0D1B2A]/5">
        <div className="flex items-center gap-2"><Package size={18} /><div className="font-display text-2xl">Meus pedidos</div></div>
        <div className="mt-4 space-y-3">
          {orders.length === 0 && <div className="text-sm text-[#6B7280]">Você ainda não fez pedidos.</div>}
          {orders.map((o) => (
            <div key={o.id} className="p-4 rounded-2xl border border-[#0D1B2A]/8 flex items-center justify-between">
              <div>
                <div className="font-medium">#{o.id.slice(0, 8)}</div>
                <div className="text-xs text-[#6B7280]">{new Date(o.created_at).toLocaleString("pt-BR")}</div>
              </div>
              <div className="text-right">
                <span className="text-xs px-2 py-1 rounded-full bg-[#F7F2E7]">{o.status}</span>
                <div className="font-display text-lg mt-1">{brl(o.total_brl)}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

const Field = ({ l, v, onC, type = "text", icon: I }) => (
  <div>
    <label className="text-sm text-[#3A4A5F] flex items-center gap-1">{I && <I size={13} className="text-[#D4AF37]" />} {l}</label>
    <input type={type} value={v ?? ""} onChange={(e) => onC(e.target.value)} className="input-luxe mt-1" />
  </div>
);
